import { useOktaAuth } from '@okta/okta-react';
import React, { useEffect, useRef, useState } from 'react';
import { Tokens, TokenResponse } from '@okta/okta-auth-js';
import pino from 'pino';
import { postData } from '@datavant/idsb-shared/utils/apiClient';
import { useRouter } from 'next/router';
import OktaSignIn, { EventContext, EventErrorContext } from '@okta/okta-signin-widget';
import { UiKit } from '@datavant/uikit';

const log = pino().child({ name: 'cb' });

const OktaWidget = () => {
    const widgetRef = useRef(null);
    const [isError, setIsError] = useState(false);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const { oktaAuth } = useOktaAuth();
    const router = useRouter();
    const [isFailedAttempt, setIsFailedAttempt] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);

    const parseTokenCB = async (tokens: Tokens) => {
        // directly copied from smartrequest/pages/cb.tsx
        log.info('Parsing');
        try {
            await postData('/api/open/token', {
                accessToken: tokens.accessToken.accessToken,
            });
            log.info('Setting on token manager');
            oktaAuth.tokenManager.setTokens(tokens);
            // clear session storage on login
            sessionStorage.clear();
        } catch (error) {
            log.error(error, 'Fetch failed');
            // TODO - report error and redirect
            return;
        }
    };

    const legacyLogin = async (): Promise<void> => {
        // clear session storage on login
        sessionStorage.clear();
        return oktaAuth.signInWithRedirect({
            redirectUri: window.location.origin + '/cb',
            originalUri: window.location.origin,
        });
    };

    const handleSignIn = async (widget: OktaSignIn, widgetRef: React.MutableRefObject<any>) => {
        const isAuthenticated = widget?.authClient && (await widget.authClient.session.exists());
        if (isAuthenticated) {
            // if user is authenticated then get the tokens without prompting the user
            widget.authClient.token
                .getWithoutPrompt()
                .then(async (tokens: TokenResponse) => {
                    setIsSigningIn(true);
                    await parseTokenCB(tokens.tokens);
                })
                .catch(() => {
                    setIsSigningIn(false);
                    setIsError(true);
                });
            // if the path contains ?fromLogin=true, then the user successfully signed in and we should redirect them
            // if they are not authenticated and somehow the path contains ?fromLogin=true, then this will send them to the
            // cioxapps sign in flow
        } else if (router.asPath.includes('?fromLogin=true')) {
            sessionStorage.clear();
            void widget.authClient.signInWithRedirect({
                originalUri: window.location.origin,
                redirectUri: window.location.origin + '/cb',
            });
        } else {
            // if user is not authenticated then show the sign in widget
            widget
                .showSignInToGetTokens({
                    el: widgetRef.current,
                })
                .then(async tokens => {
                    setIsSigningIn(true);
                    await parseTokenCB(tokens);
                })
                .catch(() => {
                    setIsSigningIn(false);
                    setIsError(true);
                });
        }
    };

    useEffect(() => {
        const widget = new OktaSignIn({
            baseUrl: oktaAuth.options.issuer.split('/oauth2')[0],
            clientId: oktaAuth.options.clientId,
            redirectUri: oktaAuth.options.redirectUri,
            authParams: {
                pkce: true,
                issuer: oktaAuth.options.issuer,
                display: 'page',
                scopes: ['openid', 'profile', 'email'],
                responseType: ['id_token', 'token', 'code'],
            },
            authClient: oktaAuth,
            useInteractionCodeFlow: false,
            useClassicEngine: true,
            features: {
                idpDiscovery: true,
                showPasswordToggleOnSignInPage: true,
                rememberMe: true,
                sameDeviceOVEnrollmentEnabled: true,
                mfaOnlyFlow: true,
                multiOptionalFactorEnroll: true,
            },
            idpDiscovery: {
                requestContext: window.location.href,
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            i18n: {
                en: {
                    // override default English language text
                    'primaryauth.username.placeholder': 'Email',
                    'primaryauth.username.tooltip': 'Email',
                    'error.username.required': 'Please enter an email',
                    'error.username.invalid': 'Please enter a valid email',
                    'password.forgot.email.or.username.placeholder': 'Email',
                    'password.forgot.email.or.username.tooltip': 'Email',
                    'help': ' ',
                },
            },
            helpLinks: {
                custom: [
                    {
                        text: 'Help',
                        href: window.location.origin + '/open/login-questions',
                    },
                ],
            },
        });

        void handleSignIn(widget, widgetRef);

        widget.on('afterError', async (_ctx: EventContext, err: EventErrorContext) => {
            if (err.xhr.responseJSON.errorCode === 'E0000004') {
                document.getElementsByClassName(
                    'okta-form-infobox-error infobox infobox-error'
                )[0].childNodes[1].textContent = 'Incorrect email or password';
                setIsFailedAttempt(true);
            }
            // Do we need other custom error messages?
        });

        // removing the widget on unmount should also clean up the event listener
        return () => widget.remove();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isFailedAttempt) {
            setFailedAttempts(failedAttempts + 1);
            // reset the failed attempt state
            setIsFailedAttempt(false);
        }
        if (failedAttempts >= 4) {
            document.getElementsByClassName(
                'okta-form-infobox-error infobox infobox-error'
            )[0].childNodes[1].textContent = `Too many failed attempts in a row may lock your account and prevent you from signing in. 
            Please make sure you're entering the correct email and password or reset your password and try again. See the help page for more details and
            contact support if you need assistance.`;
        }
    }, [isFailedAttempt]);

    return (
        <>
            <link
                href="https://global.oktacdn.com/okta-signin-widget/5.9.0/css/okta-sign-in.min.css"
                rel="stylesheet"
            />
            {isSigningIn && <UiKit.SpinnerWithText text={'Signing you in...'} mt={'0px'} />}
            {isError && (
                <UiKit.Hint.Error
                    title={'Error'}
                    textLines={[
                        'An error occurred while loading this page.',
                        'Please refresh the page and try again.',
                        'If issues persist, please contact support using the number below.',
                    ]}
                    colored={true}
                >
                    <UiKit.Text.BodyRegular>
                        Alternatively, you can try logging in{' '}
                        <span style={{ fontWeight: 800 }}>
                            <UiKit.Text.Clickable text={'  here'} onClick={legacyLogin} underline={true} />
                        </span>
                        .
                    </UiKit.Text.BodyRegular>
                </UiKit.Hint.Error>
            )}
            <div className={'okta-widget-div'} style={{ minHeight: '100px' }} ref={widgetRef} />
        </>
    );
};

export default OktaWidget;
